import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { assertProofGatedClaims, comparisonProofClaims } from '@crm/agent-ops';
import { DemoRequestForm } from '../components/forms/DemoRequestForm';
import { HomeSectionHeader } from '../components/home/HomeSectionHeader';
import { HOME_SURFACES } from '../components/home/homeSurfaces';
import { homeButtonClasses } from '../components/home/HomeButton';
import { MarketingPageHero } from '../components/marketing/MarketingPageHero';
import {
  marketingCardClass,
  marketingStatCardClass,
  marketingTableShellClass,
} from '../components/marketing/marketingSurfaces';
import {
  competitors,
  comparisonCategories,
  type ComparisonFeature,
} from '../config/comparisonData';
import { buildSiteUrl, usePageMetadata, useStructuredData } from '../lib/seo';
import { cn } from '../lib/utils';

function StatusIcon({
  status,
}: {
  status: 'yes' | 'partial' | 'no';
}): React.JSX.Element {
  if (status === 'yes') {
    return (
      <svg
        className="h-4 w-4 text-brand-teal"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2.5}
          d="M5 13l4 4L19 7"
        />
      </svg>
    );
  }

  if (status === 'partial') {
    return (
      <svg
        className="h-4 w-4 text-brand-accent"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M20 12H4"
        />
      </svg>
    );
  }

  return (
    <svg
      className="h-4 w-4 text-[var(--ir-muted)]/40"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}

function getStatusValue(
  feature: ComparisonFeature,
  competitorId: string
): 'yes' | 'partial' | 'no' {
  if (competitorId === 'inmueble_red') return feature.inmuebleRed;
  if (competitorId === 'sperant') return feature.sperant;
  return feature.salesforce;
}

function getLatestEvidenceCaptureDate(
  claims: typeof comparisonProofClaims
): string | null {
  const capturedAtTimestamps = claims
    .flatMap((claim) =>
      claim.evidence.map((evidence) => Date.parse(evidence.capturedAt))
    )
    .filter((timestamp) => Number.isFinite(timestamp));

  if (capturedAtTimestamps.length === 0) {
    return null;
  }

  return new Intl.DateTimeFormat('es-PE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Math.max(...capturedAtTimestamps)));
}

export function ComparisonPage(): React.JSX.Element {
  const { t } = useTranslation(['comparison', 'common']);

  assertProofGatedClaims();

  usePageMetadata({
    title: t('comparison:meta.title'),
    description: t('comparison:meta.description'),
    path: '/comparar',
  });

  const schema = useMemo<Record<string, unknown>>(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: t('comparison:meta.title'),
      description: t('comparison:meta.description'),
      url: buildSiteUrl('/comparar'),
    }),
    [t]
  );

  useStructuredData({
    id: 'comparison-page',
    data: schema,
  });

  const whyClaims = comparisonProofClaims.filter(
    (claim) =>
      claim.pagePath === '/comparar' &&
      claim.id !== 'comparison_migration_wedge'
  );
  const migrationClaim = comparisonProofClaims.find(
    (claim) => claim.id === 'comparison_migration_wedge'
  );
  const comparisonPageClaims = comparisonProofClaims.filter(
    (claim) => claim.pagePath === '/comparar'
  );
  const latestEvidenceCaptureDate =
    getLatestEvidenceCaptureDate(comparisonPageClaims);
  const comparisonMatrixSubtitle = latestEvidenceCaptureDate
    ? `Lectura basada en capacidades nativas y evidencia pública auditada al ${latestEvidenceCaptureDate}.`
    : 'Lectura basada en capacidades nativas y evidencia pública auditada.';

  return (
    <div className="flex flex-col bg-brand-bg">
      <MarketingPageHero
        badge={t('comparison:hero.badge')}
        title={t('comparison:hero.title')}
        subtitle={t('comparison:hero.subtitle')}
        actions={
          <>
            <a href="#demo-form" className={homeButtonClasses('accent', 'lg')}>
              {t('comparison:cta.button')}
            </a>
            <a
              href="#comparison-matrix"
              className={homeButtonClasses('outline', 'lg')}
            >
              {t('comparison:hero.viewMatrix')}
            </a>
          </>
        }
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {competitors.map((competitor) => (
            <div key={competitor.id} className={marketingStatCardClass}>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-teal">
                {competitor.name}
              </p>
              <p className="mt-3 text-sm leading-relaxed text-[var(--ir-muted)]">
                {t(competitor.descriptionKey)}
              </p>
              <p className="mt-4 text-xs font-semibold uppercase tracking-[0.12em] text-brand-primary">
                {t(competitor.highlightKey)}
              </p>
            </div>
          ))}
        </div>
      </MarketingPageHero>

      <section id="comparison-matrix" className={HOME_SURFACES.ivory}>
        <div className="section-padding !pt-0">
          <HomeSectionHeader
            badge={t('comparison:matrix.badge')}
            title={t('comparison:matrix.title')}
            subtitle={comparisonMatrixSubtitle}
            className="mb-12 md:mb-16"
          />

          <div className="hidden space-y-12 lg:block">
            {comparisonCategories.map((category) => (
              <section key={category.id}>
                <div className="mb-6 flex items-end justify-between gap-4 border-b border-brand-primary/10 pb-4">
                  <h3 className="font-serif text-3xl font-bold text-brand-primary">
                    {t(category.labelKey)}
                  </h3>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--ir-muted)]">
                    {t('comparison:matrix.columnsSubtitle')}
                  </p>
                </div>

                <div className={marketingTableShellClass}>
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-brand-primary/10 bg-brand-bg/60">
                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-[0.2em] text-[var(--ir-muted)]">
                          {t('comparison:matrix.capacity')}
                        </th>
                        {competitors.map((competitor) => (
                          <th
                            key={competitor.id}
                            className={cn(
                              'px-6 py-4 text-xs font-bold uppercase tracking-[0.2em]',
                              competitor.id === 'inmueble_red'
                                ? 'text-brand-teal'
                                : 'text-[var(--ir-muted)]'
                            )}
                          >
                            {competitor.name}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {category.features.map((feature) => (
                        <tr
                          key={feature.id}
                          className="border-b border-brand-primary/5 last:border-b-0"
                        >
                          <td className="px-6 py-5 text-base text-brand-primary">
                            {t(feature.labelKey)}
                          </td>
                          {competitors.map((competitor) => {
                            const status = getStatusValue(
                              feature,
                              competitor.id
                            );
                            return (
                              <td key={competitor.id} className="px-6 py-5">
                                <span className="inline-flex items-center gap-2 text-sm">
                                  <StatusIcon status={status} />
                                  <span
                                    className={cn(
                                      status === 'yes'
                                        ? 'font-medium text-brand-teal'
                                        : status === 'partial'
                                          ? 'font-medium text-brand-accent-ink'
                                          : 'text-[var(--ir-muted)]'
                                    )}
                                  >
                                    {t(`comparison:status.${status}`)}
                                  </span>
                                </span>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ))}
          </div>

          <div className="space-y-10 lg:hidden">
            {comparisonCategories.map((category) => (
              <section key={category.id}>
                <h3 className="mb-4 font-serif text-2xl font-bold text-brand-primary">
                  {t(category.labelKey)}
                </h3>
                <div
                  className={cn(
                    marketingCardClass,
                    'divide-y divide-brand-primary/10'
                  )}
                >
                  {category.features.map((feature) => (
                    <div key={feature.id} className="p-5">
                      <p className="text-base font-medium text-brand-primary">
                        {t(feature.labelKey)}
                      </p>
                      <div className="mt-4 grid grid-cols-3 gap-3">
                        {competitors.map((competitor) => {
                          const status = getStatusValue(feature, competitor.id);
                          return (
                            <div
                              key={competitor.id}
                              className="rounded-xl border border-brand-primary/10 bg-brand-bg/50 px-3 py-3 text-center"
                            >
                              <p
                                className={cn(
                                  'text-xs font-bold uppercase tracking-[0.12em]',
                                  competitor.id === 'inmueble_red'
                                    ? 'text-brand-teal'
                                    : 'text-[var(--ir-muted)]'
                                )}
                              >
                                {competitor.name.split('.')[0]}
                              </p>
                              <div className="mt-3 flex justify-center">
                                <StatusIcon status={status} />
                              </div>
                              <p className="mt-2 text-xs text-[var(--ir-muted)]">
                                {t(`comparison:status.${status}`)}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </section>

      <section className={HOME_SURFACES.parchment}>
        <div className="section-padding">
          <HomeSectionHeader
            badge={t('comparison:whySection.badge')}
            title={t('comparison:whySection.title')}
            subtitle={t('comparison:whySection.subtitle')}
            className="mb-12 md:mb-16"
          />

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {whyClaims.map((claim) => (
              <article key={claim.id} className={cn(marketingCardClass, 'p-8')}>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-accent-ink">
                  {t('comparison:proofGate.label')}
                </p>
                <h3 className="mt-4 font-serif text-3xl font-bold text-brand-primary">
                  {t(claim.titleKey)}
                </h3>
                <p className="mt-4 text-base leading-relaxed text-[var(--ir-muted)]">
                  {t(claim.bodyKey)}
                </p>
                <p className="mt-6 text-xs font-bold uppercase tracking-[0.18em] text-[var(--ir-muted)]">
                  {t('comparison:proofGate.subtext', {
                    proofs: claim.proofIds.length,
                    sources: claim.evidence.length,
                  })}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {migrationClaim ? (
        <section className={HOME_SURFACES.deep}>
          <div className="section-padding">
            <div className="max-w-4xl">
              <HomeSectionHeader
                badge={t('comparison:migrationWedge.badge')}
                title={t(migrationClaim.titleKey)}
                subtitle={t(migrationClaim.bodyKey)}
                light
                className="mb-8"
              />
              <p className="mb-10 text-xs font-bold uppercase tracking-[0.18em] text-white/70">
                {t('comparison:proofGate.subtext', {
                  proofs: migrationClaim.proofIds.length,
                  sources: migrationClaim.evidence.length,
                })}
              </p>
              <a
                href="#demo-form"
                className={homeButtonClasses('accent', 'lg')}
              >
                {t('comparison:migrationWedge.cta')}
              </a>
            </div>
          </div>
        </section>
      ) : null}

      <section id="demo-form" className={HOME_SURFACES.parchment}>
        <div className="section-padding">
          <div className="grid grid-cols-1 items-start gap-16 lg:grid-cols-2 lg:gap-24">
            <div>
              <HomeSectionHeader
                badge={t('comparison:contact.badge')}
                title={t('comparison:cta.title')}
                subtitle={t('comparison:cta.subtitle')}
                className="mb-8"
              />
              <p className="text-sm font-semibold uppercase tracking-[0.16em] text-brand-teal">
                {t('comparison:contact.highlight')}
              </p>
            </div>

            <div className={cn(marketingCardClass, 'p-8 md:p-12')}>
              <DemoRequestForm
                variant="minimal"
                appearance="underlined"
                submitLabel={t('comparison:cta.button')}
                helperText={t('comparison:contact.helperText')}
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
